import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { TrackedTranslation } from '@ps/web-core-intl';
import {
  GERMANY_NAME_ALLOWED_CHARACTERS,
  GERMANY_NAME_VALID_CHARACTERS,
  NAME_AND_CITY_NOT_ALLOWED_SYMBOLS,
  mustContainLetter,
  mustContainOnlyLatinCharacters,
  mustNotContainNumber,
  mustNotContainSymbols,
  noConsecutiveHyphens,
  noConsecutiveSpaces,
  noWhiteStartOrEnd,
  required,
  validateAllowedCharacters
} from '@ps/web-shared-validators';
import type { FieldMetaState } from 'react-final-form';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import TextInput from '../../../../../TextInput/TextInput';

import {
  TXTCLI_The_last_name_you_have_entered_ELL,
  TXTWCL_Add_a_single_hyphen_if_itXs_mi_ELL,
  TXTWCL_Last_name,
  TXTWCL_Last_nameXsXX,
  TXTWCL_Please_enter_your_last_nameX,
  TXTWCL_Please_enter_your_surnameX,
  TXTWCL_Surname
} from './FamilyName.messages.json';

const fieldName = 'lastName';
const maxLength = 50;
const DE_MAX_LENGTH = 85;
const deValidators = [
  validateAllowedCharacters(GERMANY_NAME_ALLOWED_CHARACTERS, GERMANY_NAME_VALID_CHARACTERS),
  noConsecutiveSpaces,
  noConsecutiveHyphens
];
const grValidators = [
  mustContainOnlyLatinCharacters(),
  noConsecutiveSpaces,
  mustNotContainSymbols(
    NAME_AND_CITY_NOT_ALLOWED_SYMBOLS,
    TXTCLI_The_last_name_you_have_entered_ELL
  )
];
const siteSpecificLabel: Partial<Record<Sites, TrackedTranslation>> = {
  [Sites.CA_ON]: TXTWCL_Surname,
  [Sites.POR]: TXTWCL_Last_nameXsXX
};

const getLabel = (siteId: Sites) => siteSpecificLabel[siteId] ?? TXTWCL_Last_name;

const getRequiredLabel = (siteId: Sites) => {
  switch (siteId) {
    case Sites.CA_ON:
      return TXTWCL_Please_enter_your_surnameX;
    case Sites.DE:
      return [TXTWCL_Please_enter_your_last_nameX, TXTWCL_Add_a_single_hyphen_if_itXs_mi_ELL];
    default:
      return TXTWCL_Please_enter_your_last_nameX;
  }
};

interface Props {
  meta?: FieldMetaState<string>;
}

const FamilyName: React.FC<Props> = ({ meta }) => {
  const siteId = useSelector(select.cms.getSiteId);
  const isDe = siteId === Sites.DE;
  const isGr = siteId === Sites.GR;

  return (
    <TextInput
      id={fieldName}
      meta={{ ...meta, touched: true }}
      label={getLabel(siteId)}
      maxLength={isDe ? DE_MAX_LENGTH : maxLength}
      checkDisabled
      validators={[
        mustContainLetter(TXTCLI_The_last_name_you_have_entered_ELL),
        noWhiteStartOrEnd,
        required(getRequiredLabel(siteId)),
        mustNotContainNumber(TXTCLI_The_last_name_you_have_entered_ELL),
        ...(isDe
          ? deValidators
          : isGr
          ? grValidators
          : [
              mustNotContainSymbols(
                NAME_AND_CITY_NOT_ALLOWED_SYMBOLS,
                TXTCLI_The_last_name_you_have_entered_ELL
              )
            ])
      ]}
    />
  );
};

export default FamilyName;
